import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  ChevronDown,
  FileDown,
  FileSpreadsheet,
  RefreshCw,
  RotateCcw,
  UsersRound,
} from "lucide-react";
import ScreenHeader from "../components/layout/ScreenHeader";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FilterCard,
  FilterToggle,
  Notice,
  PersonCell,
  SelectInput,
  StatusPill,
  TableSkeleton,
  TextInput,
} from "../ui";
import { exportToExcel, exportToPDF } from "../utils/exportUtils";

const API_URL = process.env.REACT_APP_API_URL;

const BONUS_STATUS = {
  granted: { tone: "success", label: "Bonus received" },
  pending: { tone: "warning", label: "Pending check-in" },
  "not-eligible": { tone: "neutral", label: "Not eligible" },
};

const formatShortDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "N/A";

const AffiliatedReports = () => {
  // Issue 6 Fix: Use userLogin instead of adminLogin
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  // State
  const [loading, setLoading] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [affiliates, setAffiliates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch User's Clubs
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const config = {
          headers: { Authorization: `Bearer ${userInfo?.token}` },
        };
        // Issue 8 Fix: Use API_URL directly (already includes /api)
        const { data } = await axios.get(`${API_URL}/clubs`, config);

        const clubList = data.clubs || data.data || data;

        const activeClubs = Array.isArray(clubList)
          ? clubList.filter((c) => c.isActive)
          : [];
        setClubs(activeClubs);

        if (activeClubs.length > 0) {
          setSelectedClub(activeClubs[0]._id);
        }
      } catch (err) {
        console.error("Failed to fetch clubs", err);
        if (userInfo?.assignedClubs && userInfo.assignedClubs.length > 0) {
          if (typeof userInfo.assignedClubs[0] === "object") {
            setClubs(userInfo.assignedClubs);
            setSelectedClub(userInfo.assignedClubs[0]._id);
          }
        } else {
          setError("Could not load clubs. Please try again.");
        }
      }
    };

    if (userInfo) {
      fetchClubs();
    }
  }, [userInfo]);

  // Fetch Affiliates for Selected Club
  useEffect(() => {
    const fetchAffiliates = async () => {
      if (!selectedClub) return;

      try {
        setLoading(true);
        setError("");
        const config = {
          headers: { Authorization: `Bearer ${userInfo?.token}` },
          params: {
            ...(fromDate && { fromDate }),
            ...(toDate && { toDate }),
          },
        };

        // Issue 8 Fix: Use API_URL directly
        const { data } = await axios.get(
          `${API_URL}/users/affiliates/club/${selectedClub}`,
          config,
        );

        setAffiliates(data.affiliates);
        setSummary(data.summary || null);
      } catch (err) {
        console.error("Failed to fetch affiliates", err);
        setError(
          err.response?.data?.message || "Failed to fetch affiliated reports",
        );
        setAffiliates([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliates();
  }, [selectedClub, userInfo, fromDate, toDate, refreshKey]);

  const selectedClubName =
    clubs.find((c) => c._id === selectedClub)?.name || "AllClubs";

  // Export Handlers
  const prepareExportData = () => {
    return affiliates.map((affiliate) => ({
      "Affiliate Name": affiliate.name || "Unknown",
      Contact: affiliate.phoneNumber || "N/A",
      "Referred By": affiliate.referredBy?.name || "N/A",
      Referrals: affiliate.totalReferrals || 0,
      "Code Referrals": affiliate.codeReferrals?.length || 0,
      "Total Earnings": affiliate.totalEarnings || 0,
      "Pending Earnings": affiliate.pendingEarnings || 0,
    }));
  };

  const exportColumns = [
    { title: "Affiliate Name", field: "Affiliate Name" },
    { title: "Contact", field: "Contact" },
    { title: "Referred By", field: "Referred By" },
    { title: "Referrals", field: "Referrals" },
    { title: "Code Referrals", field: "Code Referrals" },
    { title: "Total Earnings", field: "Total Earnings" },
    { title: "Pending Earnings", field: "Pending Earnings" },
  ];

  const handleExportExcel = () => {
    exportToExcel(
      prepareExportData(),
      exportColumns,
      `Affiliate_Report_${selectedClubName.replace(/\s+/g, "_")}`,
    );
  };

  const handleExportPDF = () => {
    exportToPDF(
      prepareExportData(),
      exportColumns,
      `Affiliate_Report_${selectedClubName.replace(/\s+/g, "_")}`,
      `Affiliated Reports - ${selectedClubName}`,
    );
  };

  const handleRefresh = () => setRefreshKey((key) => key + 1);

  const handleResetDates = () => {
    setFromDate("");
    setToDate("");
  };

  const hasDateFilter = Boolean(fromDate || toDate);
  const filterChips = [
    selectedClub && { icon: Building2, label: selectedClubName },
    hasDateFilter && {
      icon: CalendarDays,
      label: `${fromDate || "…"} → ${toDate || "…"}`,
    },
  ].filter(Boolean);

  const exportDisabled = loading || affiliates.length === 0;

  return (
    <div className="flex flex-col gap-5 min-w-0">
      <ScreenHeader
        title="Affiliated reports"
        description="How the affiliates assigned to your clubs are performing."
        actions={
          <>
            <FilterToggle
              open={filtersOpen}
              onToggle={() => setFiltersOpen((o) => !o)}
              count={hasDateFilter ? 1 : 0}
              controls="affiliate-filters"
            />
            <Button variant="secondary" icon={RefreshCw} loading={loading} disabled={!selectedClub} onClick={handleRefresh}>
              Refresh
            </Button>
            <Button variant="secondary" icon={FileSpreadsheet} disabled={exportDisabled} onClick={handleExportExcel}>
              Excel
            </Button>
            <Button variant="secondary" icon={FileDown} disabled={exportDisabled} onClick={handleExportPDF}>
              PDF
            </Button>
          </>
        }
      />

      {error && (
        <Notice tone="danger" icon={AlertTriangle}>
          {error}
        </Notice>
      )}

      <FilterCard
        id="affiliate-filters"
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        chips={filterChips}
        emptyLabel="All referral dates"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(0,220px))_auto] gap-3 items-end">
          <SelectInput
            label="Club"
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            placeholder={clubs.length === 0 ? "No clubs" : undefined}
            options={clubs.map((club) => ({ value: club._id, label: club.name }))}
          />
          <TextInput
            label="From"
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <TextInput
            label="To"
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
          />
          <div className="flex">
            <Button variant="ghost" icon={RotateCcw} disabled={!hasDateFilter} onClick={handleResetDates}>
              Reset
            </Button>
          </div>
        </div>
      </FilterCard>

      <Card padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 sm:px-5 border-b border-cm-border">
          <div>
            <h3 className="text-base font-bold text-cm-text m-0">Affiliate performance</h3>
            <p className="text-[13px] text-cm-muted m-0">Open an affiliate to see the members they referred.</p>
          </div>
          {!loading && affiliates.length > 0 && (
            <Badge>{affiliates.length} affiliate{affiliates.length === 1 ? "" : "s"}</Badge>
          )}
        </div>

        {loading ? (
          <TableSkeleton rows={5} cols={5} />
        ) : affiliates.length === 0 ? (
          <EmptyState
            compact
            icon={UsersRound}
            title="No affiliates for this club"
            description={fromDate || toDate
              ? "No players joined through an affiliate in this date range."
              : "Affiliates appear here once players who first checked in at this club join with their referral code."}
          />
        ) : (
          <>
            {/* Column headings (desktop) */}
            <div className="hidden md:grid grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1fr)_40px] gap-4 px-5 py-2 bg-cm-surface-2 border-b border-cm-border text-xs font-semibold text-cm-muted">
              <span>Affiliate</span>
              <span>Referred by</span>
              <span className="text-right">Referrals</span>
              <span />
            </div>
            <ul className="m-0 p-0 list-none">
              {affiliates.map((affiliate) => (
                <AffiliateRow key={affiliate._id} affiliate={affiliate} />
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
};

const AffiliateRow = ({ affiliate }) => {
  const [open, setOpen] = useState(false);
  const codeReferrals = affiliate.codeReferrals || [];
  const canExpand = codeReferrals.length > 0;

  const handleExpand = () => setOpen(!open);

  return (
    <li className="border-b border-cm-border last:border-b-0">
      <div className="grid grid-cols-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1fr)_40px] items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-5">
        <div className="col-span-2 md:col-span-1 min-w-0">
          <PersonCell name={affiliate.name || "Unknown"} sub={affiliate.phoneNumber} />
        </div>
        <div className="min-w-0">
          <span className="md:hidden text-xs text-cm-muted mr-1">Referred by</span>
          {affiliate.referredBy?.name ? <Badge tone="info">{affiliate.referredBy.name}</Badge> : <span className="text-cm-faint">—</span>}
        </div>
        <div className="text-right tabular-nums text-sm">
          <span className="md:hidden text-xs text-cm-muted mr-1">Referrals</span>
          {codeReferrals.length}
        </div>
        <div className="flex justify-end">
          {canExpand && (
            <button
              type="button"
              onClick={handleExpand}
              aria-expanded={open}
              aria-label={open ? "Hide referrals" : "Show referrals"}
              title={open ? "Hide referrals" : "Show referrals"}
              className="grid h-8 w-8 place-items-center rounded-cm text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text"
            >
              <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="mx-4 mb-3 sm:mx-5 flex flex-col gap-3">
          <div className="rounded-cm-lg border border-cm-border bg-cm-surface-2 p-3">
            <p className="text-xs font-semibold text-cm-muted m-0 mb-2">
              Joined with referral code{affiliate.referralCode ? ` ${affiliate.referralCode}` : ""} ({codeReferrals.length})
            </p>
            {codeReferrals.length > 0 ? (
              <ul className="m-0 p-0 list-none flex flex-col divide-y divide-cm-border">
                {codeReferrals.map((ref) => {
                  const bonus = BONUS_STATUS[ref.bonusStatus] || BONUS_STATUS["not-eligible"];
                  return (
                    <li key={ref._id} className="grid gap-2 py-2 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.3fr)] md:items-center">
                      <PersonCell size={28} name={ref.name || "Unknown"} sub={ref.phoneNumber || "N/A"} />
                      <div className="text-xs text-cm-muted">
                        <span className="md:hidden mr-1">Account created</span>
                        {formatShortDate(ref.createdAt)}
                      </div>
                      <div className="flex flex-col items-start gap-0.5">
                        <StatusPill tone={bonus.tone} size="sm">{bonus.label}</StatusPill>
                        {ref.bonusGrantedAt && (
                          <span className="text-xs text-cm-muted">{formatShortDate(ref.bonusGrantedAt)}</span>
                        )}
                      </div>
                      <div className="text-xs">
                        {ref.firstCheckIn ? (
                          <>
                            <span className="font-semibold text-cm-text">{ref.firstCheckIn.clubName || "Unknown club"}</span>
                            <span className="text-cm-muted"> · first check-in {formatShortDate(ref.firstCheckIn.checkedInAt)}</span>
                          </>
                        ) : (
                          <span className="text-cm-muted">Not checked in yet</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-cm-muted m-0">No players joined with this referral code.</p>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export default AffiliatedReports;
